// Offline Storage Service using IndexedDB
export interface OfflineObservation {
  id: string;
  type: 'create' | 'update';
  observationId?: string; // for updates
  observationData: Record<string, any>;
  photos: File[];
  media: File[];
  mediaTypes: string[];
  mediaThumbnails?: (File | null)[]; // Store thumbnails for videos (null for audio/HEVC)
  photosToDelete?: number[];
  mediaToDelete?: number[];
  projectId: string | number;
  taskId: string | number;
  geometry?: { lng: number; lat: number };
  layerId?: string | number;
  auth?: { authUsername?: string; authPassword?: string; authToken?: string; customerId?: string };
  timestamp: number;
  retryCount: number;
  syncStatus?: 'pending' | 'syncing'; // Track sync state for cross-tab safety
  syncClaimedAt?: number; // Timestamp when a tab claimed this record for syncing
}

// iOS Safari cannot serialize File/Blob objects in IndexedDB (DataCloneError: BlobURLs are not yet supported).
// We store files as ArrayBuffer + metadata and reconstruct File objects on read-back.
interface SerializedFile {
  name: string;
  type: string;
  lastModified: number;
  data: ArrayBuffer;
}

// Internal storage shape: File[] replaced with SerializedFile[]
interface StoredOfflineObservation extends Omit<OfflineObservation, 'photos' | 'media' | 'mediaThumbnails'> {
  photos: SerializedFile[];
  media: SerializedFile[];
  mediaThumbnails?: (SerializedFile | null)[];
}

async function serializeFile(file: File): Promise<SerializedFile> {
  const data = await file.arrayBuffer();
  return { name: file.name, type: file.type, lastModified: file.lastModified, data };
}

function deserializeFile(sf: SerializedFile): File {
  return new File([sf.data], sf.name, { type: sf.type, lastModified: sf.lastModified });
}

async function serializeObservation(obs: OfflineObservation): Promise<StoredOfflineObservation> {
  const photos = await Promise.all(obs.photos.map(serializeFile));
  const media = await Promise.all(obs.media.map(serializeFile));
  const mediaThumbnails = obs.mediaThumbnails
    ? await Promise.all(obs.mediaThumbnails.map(f => (f ? serializeFile(f) : Promise.resolve(null))))
    : undefined;
  return { ...obs, photos, media, mediaThumbnails };
}

function deserializeObservation(stored: StoredOfflineObservation): OfflineObservation {
  const photos = stored.photos.map(deserializeFile);
  const media = stored.media.map(deserializeFile);
  const mediaThumbnails = stored.mediaThumbnails
    ? stored.mediaThumbnails.map(sf => (sf ? deserializeFile(sf) : null))
    : undefined;
  return { ...stored, photos, media, mediaThumbnails };
}

class OfflineStorageService {
  private dbName = 'FaunaObservationsOffline';
  private version = 3;
  private storeName = 'pending_observations';
  private staleThresholdMs = 60000; // 60 seconds — records stuck in 'syncing' longer than this are reclaimed

  // Initialize IndexedDB
  private async openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = (event) => {
        const error = (event.target as IDBOpenDBRequest).error;
        console.error('IndexedDB error:', error);

        // Handle version error specifically
        if (error?.name === 'VersionError') {
          console.warn('IndexedDB version conflict detected. Database may need to be recreated.');
        }

        reject(error);
      };

      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const oldVersion = event.oldVersion;
        // Create object store if it doesn't exist (fresh install or version 0→3)
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('type', 'type', { unique: false });
          store.createIndex('syncStatus', 'syncStatus', { unique: false });
        } else if (oldVersion < 3) {
          // Upgrading from version 2 → 3: add syncStatus index
          const transaction = (event.target as IDBOpenDBRequest).transaction!;
          const store = transaction.objectStore(this.storeName);
          if (!store.indexNames.contains('syncStatus')) {
            store.createIndex('syncStatus', 'syncStatus', { unique: false });
          }
        }
      };

      request.onblocked = () => {
        console.warn('IndexedDB upgrade blocked. Please close other tabs with this app open.');
      };
    });
  }

  // Development helper: Delete database to resolve version conflicts
  async resetDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      const deleteRequest = indexedDB.deleteDatabase(this.dbName);

      deleteRequest.onerror = () => {
        console.error('Failed to delete IndexedDB:', deleteRequest.error);
        reject(deleteRequest.error);
      };

      deleteRequest.onsuccess = () => {
        resolve();
      };

      deleteRequest.onblocked = () => {
        console.warn('Delete blocked. Please close other tabs with this app.');
      };
    });
  }

  // Save observation for offline sync
  async saveOfflineObservation(observation: Omit<OfflineObservation, 'id' | 'timestamp' | 'retryCount'>): Promise<void> {
    const offlineObs: OfflineObservation = {
      ...observation,
      id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      retryCount: 0,
      syncStatus: 'pending',
      syncClaimedAt: undefined,
    };

    // Serialize File objects to ArrayBuffer before storing — iOS Safari cannot clone File/Blob in IDB
    const storedObs = await serializeObservation(offlineObs);

    const db = await this.openDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise<void>((resolve, reject) => {
      const request = store.add(storedObs);
      request.onerror = () => {
        console.error('Error adding observation to IndexedDB:', request.error);
        reject(request.error);
      };
      request.onsuccess = () => {
        // Update pending count in localStorage for UI display
        this.updatePendingCountDisplay();
        resolve();
      };
    });
  }

  // Get all pending observations (ordered by timestamp)
  async getPendingObservations(): Promise<OfflineObservation[]> {
    const db = await this.openDB();
    const transaction = db.transaction([this.storeName], 'readonly');
    const store = transaction.objectStore(this.storeName);
    const index = store.index('timestamp');

    return new Promise((resolve, reject) => {
      const request = index.getAll();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve((request.result as StoredOfflineObservation[]).map(deserializeObservation));
    });
  }

  // Remove observation after successful sync
  async removeOfflineObservation(id: string): Promise<void> {
    const db = await this.openDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise<void>((resolve, reject) => {
      const request = store.delete(id);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.updatePendingCountDisplay();
        resolve();
      };
    });
  }

  // Update retry count for failed sync attempts and reset syncStatus back to 'pending'.
  // Both fields are updated in the same transaction so the record is never left stuck as 'syncing'.
  async updateRetryCount(id: string, retryCount: number): Promise<void> {
    const db = await this.openDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    // Get the observation first
    const getRequest = store.get(id);

    return new Promise<void>((resolve, reject) => {
      getRequest.onerror = () => reject(getRequest.error);
      getRequest.onsuccess = () => {
        const observation = getRequest.result;
        if (observation) {
          observation.retryCount = retryCount;
          // Always reset sync status when updating retry count — the sync attempt is over
          observation.syncStatus = 'pending';
          observation.syncClaimedAt = undefined;
          const putRequest = store.put(observation);
          putRequest.onerror = () => reject(putRequest.error);
          putRequest.onsuccess = () => resolve();
        } else {
          resolve(); // Observation not found, nothing to update
        }
      };
    });
  }

  // Atomically claim all pending observations for syncing.
  // In a single readwrite transaction, reads all records with syncStatus 'pending'
  // (or missing syncStatus for legacy records) and marks them as 'syncing'.
  // This prevents a second tab from re-reading the same records.
  async claimPendingObservations(): Promise<OfflineObservation[]> {
    const db = await this.openDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const allRecords: StoredOfflineObservation[] = request.result;
        const now = Date.now();
        const claimed: StoredOfflineObservation[] = [];

        for (const record of allRecords) {
          // Claim records that are 'pending', have no syncStatus (legacy), or are stale 'syncing'
          const isPending = !record.syncStatus || record.syncStatus === 'pending';
          const isStale = record.syncStatus === 'syncing' &&
            record.syncClaimedAt != null &&
            (now - record.syncClaimedAt) > this.staleThresholdMs;

          if (isPending || isStale) {
            record.syncStatus = 'syncing';
            record.syncClaimedAt = now;
            store.put(record); // update in the same transaction
            claimed.push(record);
          }
        }

        // Wait for the transaction to complete before resolving
        // Deserialize after the transaction completes so we don't hold it open during async work
        transaction.oncomplete = () => resolve(claimed.map(deserializeObservation));
        transaction.onerror = () => reject(transaction.error);
      };
    });
  }

  // Reset the syncStatus of an observation back to 'pending' (e.g. after a sync failure)
  async resetSyncStatus(id: string): Promise<void> {
    const db = await this.openDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    const getRequest = store.get(id);

    return new Promise<void>((resolve, reject) => {
      getRequest.onerror = () => reject(getRequest.error);
      getRequest.onsuccess = () => {
        const observation = getRequest.result;
        if (observation) {
          observation.syncStatus = 'pending';
          observation.syncClaimedAt = undefined;
          const putRequest = store.put(observation);
          putRequest.onerror = () => reject(putRequest.error);
          putRequest.onsuccess = () => resolve();
        } else {
          resolve();
        }
      };
    });
  }

  // Clear all offline data (for testing or reset)
  async clearAllOfflineData(): Promise<void> {
    const db = await this.openDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise<void>((resolve, reject) => {
      const request = store.clear();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.updatePendingCountDisplay();
        resolve();
      };
    });
  }

  // Update the pending count display in localStorage
  private async updatePendingCountDisplay(): Promise<void> {
    try {
      const pending = await this.getPendingObservations();
      localStorage.setItem('pendingCount', pending.length.toString());
    } catch (error) {
      console.error('Error updating pending count display:', error);
    }
  }
}

const offlineStorageService = new OfflineStorageService();
export default offlineStorageService;